import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button, DialogContent, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { parseRemotePlayLink } from '../../../../utils/hismithLink';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

interface AddDeviceDialogProps {
    open: boolean;
    mode: 'add' | 'replace';
    isSaving: boolean;
    onClose: () => void;
    onSubmit: (link: string, nickName: string) => Promise<void>;
}

const AddDeviceDialog = ({ open, mode, isSaving, onClose, onSubmit }: AddDeviceDialogProps) => {
    const [link, setLink] = useState<string>('');
    const [nickName, setNickName] = useState<string>('');
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (open) {
            setLink('');
            setNickName('');
            setError('');
        }
    }, [open]);

    const token = useMemo(() => parseRemotePlayLink(link), [link]);
    const showInvalid = link.trim() !== '' && token === null;

    const handleSubmit = async () => {
        if (!token) return;

        setError('');

        try {
            await onSubmit(link.trim(), nickName.trim());
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save.');
        }
    };

    return (
        <DialogAdaptive open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <StyledCardTitleBar title={mode === 'add' ? 'Add Hismith Device' : 'Update Remote Play Link'} />
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        In the HISMITH app open your machine, choose <b>Remote Play</b>, and copy the link it shows (it looks like
                        <code> https://app.hismiths.com/rplayXXXXXXX</code>). Keep the Remote Play screen open while streaming; if you leave
                        it the link stops working and you will need to paste a new one.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        label="Remote Play link"
                        value={link}
                        onChange={e => setLink(e.target.value)}
                        fullWidth
                        autoFocus
                        error={showInvalid}
                        helperText={showInvalid ? 'That does not look like a Remote Play link.' : token ? `Token …${token.slice(-3)}` : ' '}
                    />

                    {mode === 'add' && (
                        <TextField
                            label="Nickname (optional)"
                            value={nickName}
                            onChange={e => setNickName(e.target.value.slice(0, 60))}
                            fullWidth
                            helperText="Shown in your device list and tip ranges."
                        />
                    )}
                </Stack>
            </DialogContent>
            <StyledDialogActions>
                <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <LoadingButton variant="contained" onClick={handleSubmit} loading={isSaving} disabled={!token}>
                    <span>{mode === 'add' ? 'Add Device' : 'Update Link'}</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default AddDeviceDialog;
